import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const TrainingService = {
    getTrainings: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/trainings`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching trainings:", error);
            throw error;
        }
    },
    
    getEmployeeTrainings: async (empNo, companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/trainings/employee/${empNo}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching employee trainings:", error);
            throw error;
        }
    },
    
    getEmployees: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/employees`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching employees:", error);
            throw error;
        }
    },
    
    addTraining: async (training, companyId, token, userType) => {
        try {
            const response = await axios.post(`${API_URL}/trainings`, training, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error adding training:", error);
            throw error;
        }
    },
    
    deleteTraining: async (id, companyId, token, userType) => {
        try {
            await axios.delete(`${API_URL}/trainings/${id}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
        } catch (error) {
            console.error("Error deleting training:", error);
            throw error;
        }
    }
};

const EmployeeTraining = () => {
    const { user, userType, companyId, logout } = useAuth();
    const isAdmin = userType === "user";
    const [trainings, setTrainings] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        empNo: "",
        trainingName: "",
        trainer: "",
        startDate: "",
        endDate: "",
        status: "",
    });

    const fetchTrainings = useCallback(async () => {
        if (!companyId) {
            logout();
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let data;
            if (isAdmin) {
                data = await TrainingService.getTrainings(companyId, token, userType);
            } else if (user) {
                data = await TrainingService.getEmployeeTrainings(user.empNo, companyId, token, userType);
            }
            setTrainings(data || []);
            setError(null);
        } catch (err) {
            console.error("Error fetching trainings:", err);
            setError("Failed to fetch trainings. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [isAdmin, user, companyId, userType, logout]);

    const fetchEmployees = useCallback(async () => {
        if (!companyId || !isAdmin) return;

        try {
            const token = localStorage.getItem('token');
            const data = await TrainingService.getEmployees(companyId, token, userType);
            setEmployees(data);
        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    }, [isAdmin, companyId, userType]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!companyId) {
            logout();
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const trainingData = {
                ...form,
                assignedBy: user.email || user.name,
            };
            await TrainingService.addTraining(trainingData, companyId, token, userType);
            setForm({
                empNo: "",
                trainingName: "",
                trainer: "",
                startDate: "",
                endDate: "",
                status: "",
            });
            fetchTrainings();
        } catch (err) {
            setError("Failed to add training. Please try again.");
        }
    };

    const handleTrainingDelete = async (id) => {
        if (!companyId) {
            logout();
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await TrainingService.deleteTraining(id, companyId, token, userType);
            setTrainings(trainings.filter(training => training.id !== id));
        } catch (err) {
            setError("Failed to delete training. Please try again.");
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchTrainings();
            fetchEmployees();
        }
    }, [companyId, fetchTrainings, fetchEmployees]);

    if (loading && !user) {
        return (
            <div className="w-full p-6 flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
                <p className="text-white text-xl">Loading...</p>
            </div>
        );
    }

    return (
        <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
            <h1 className="text-4xl font-bold text-white mb-6 text-center">Employee Training Management</h1>

            {companyId ? (
                <>
                    {isAdmin ? (
                        <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-gray-700 mb-4">Admin Privileges</h3>
                            <p className="text-gray-600">You have admin access to add and manage training records for all employees.</p>
                        </div>
                    ) : (
                        <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-gray-700 mb-4">Employee View</h3>
                            <p className="text-gray-600">
                                Welcome, {user?.name}. You can view your assigned training records below.
                                Only administrators can allocate training to employees.
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-4xl mx-auto mb-4">
                            <p>{error}</p>
                        </div>
                    )}

                    {isAdmin && (
                        <form
                            onSubmit={handleFormSubmit}
                            className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto"
                        >
                            <h3 className="text-xl font-bold text-gray-700 mb-4">Assign Training</h3>
                            
                            <select
                                name="empNo"
                                value={form.empNo}
                                onChange={(e) => setForm({ ...form, empNo: e.target.value })}
                                required
                                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Employee</option>
                                {employees.map((employee) => (
                                    <option key={employee.id} value={employee.empNo}>
                                        {employee.name} ({employee.empNo})
                                    </option>
                                ))}
                            </select>
                            
                            <input
                                name="trainingName"
                                placeholder="Training Name"
                                value={form.trainingName}
                                onChange={(e) => setForm({ ...form, trainingName: e.target.value })}
                                required
                                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            
                            <input
                                name="trainer"
                                placeholder="Trainer"
                                value={form.trainer}
                                onChange={(e) => setForm({ ...form, trainer: e.target.value })}
                                required
                                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            
                            <div className="relative mb-4">
                                <input
                                    id="startDate"
                                    type="date"
                                    name="startDate"
                                    value={form.startDate}
                                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <label
                                    htmlFor="startDate"
                                    className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-500"
                                >
                                    Start Date
                                </label>
                            </div>

                            <div className="relative mb-4">
                                <input
                                    id="endDate"
                                    type="date"
                                    name="endDate"
                                    value={form.endDate}
                                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <label
                                    htmlFor="endDate"
                                    className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-500"
                                >
                                    End Date
                                </label>
                            </div>
                            
                            <select
                                name="status"
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                required
                                className="w-full p-3 border border-gray-300 rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Status</option>
                                <option value="Pending">Pending</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                            
                            <button
                                type="submit"
                                className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
                            >
                                Assign Training
                            </button>
                        </form>
                    )}

                    <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto">
                        <h3 className="text-xl font-bold text-gray-700 mb-4">
                            {isAdmin ? "All Training Records" : "My Training Records"}
                        </h3>
                        {loading ? (
                            <div className="text-center py-4">Loading training data...</div>
                        ) : (
                            <div className="overflow-x-auto max-h-[400px] scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-gray-200">
                                <table className="w-full min-w-max whitespace-nowrap border-collapse bg-white rounded-lg shadow-md">
                                    <thead>
                                        <tr className="bg-blue-600 text-white">
                                            <th className="p-3 text-left font-semibold">Employee No.</th>
                                            <th className="p-3 text-left font-semibold">Training Name</th>
                                            <th className="p-3 text-left font-semibold">Trainer</th>
                                            <th className="p-3 text-left font-semibold">Start Date</th>
                                            <th className="p-3 text-left font-semibold">End Date</th>
                                            <th className="p-3 text-left font-semibold">Status</th>
                                            {isAdmin && <th className="p-3 text-center font-semibold">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trainings.length > 0 ? (
                                            trainings.map((training) => {
                                                const employee = employees.find(emp => emp.empNo === training.empNo);
                                                return (
                                                    <tr
                                                        key={training.id}
                                                        className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                                                    >
                                                        <td className="p-3 border-b border-gray-200">
                                                            {training.empNo}
                                                            {employee && <div className="text-xs text-gray-500">{employee.name}</div>}
                                                        </td>
                                                        <td className="p-3 border-b border-gray-200">{training.trainingName}</td>
                                                        <td className="p-3 border-b border-gray-200">{training.trainer}</td>
                                                        <td className="p-3 border-b border-gray-200">
                                                            {new Date(training.startDate).toLocaleDateString()}
                                                        </td>
                                                        <td className="p-3 border-b border-gray-200">
                                                            {new Date(training.endDate).toLocaleDateString()}
                                                        </td>
                                                        <td className="p-3 border-b border-gray-200">
                                                            <span className={`px-2 py-1 rounded-full text-sm ${
                                                                training.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                                training.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                                                training.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-yellow-100 text-yellow-800'
                                                            }`}>
                                                                {training.status}
                                                            </span>
                                                        </td>
                                                        {isAdmin && (
                                                            <td className="p-3 text-center border-b border-gray-200">
                                                                <button
                                                                    onClick={() => handleTrainingDelete(training.id)}
                                                                    className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition duration-300"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={isAdmin ? 7 : 6} className="p-3 text-center text-gray-500">
                                                    No training records found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="text-center text-lg text-white">
                    Company information not available. Please login again.
                </div>
            )}
        </div>
    );
};

export default EmployeeTraining;